import { useState } from "react";
import Icon, { type IconName } from "../../components/Icon";
import StepHeader from "../../components/StepHeader";
import type { Priority, PrinterCapability, ShipmentPreferences } from "@shared/types";

interface Props {
  initial: ShipmentPreferences | null;
  submitting: boolean;
  onSubmit: (preferences: ShipmentPreferences) => void;
  onBack: () => void;
}

const PRIORITIES: { value: Priority; label: string; icon: IconName }[] = [
  { value: "CHEAPEST", label: "Cheapest", icon: "coin" },
  { value: "FASTEST", label: "Fastest", icon: "zap" },
  { value: "BALANCED", label: "Best value", icon: "star" },
  { value: "TRACKED", label: "Fully tracked", icon: "shield-check" },
  { value: "NEAREST_DROPOFF", label: "Nearest drop-off", icon: "map-pin" },
  { value: "COLLECTION_FROM_HOME", label: "Collection from home", icon: "home" },
  { value: "NO_PREFERENCE", label: "No preference", icon: "sliders" },
];

// Step 6 — preferences: what matters most, and printer availability. These
// are persisted and used by the server to filter collection, drop-off, and
// printer-compatible services before they are displayed.
export default function PreferencesStep({ initial, submitting, onSubmit, onBack }: Props) {
  const [priority, setPriority] = useState<Priority>(initial?.priority ?? "BALANCED");
  const [printer, setPrinter] = useState<PrinterCapability>(initial?.printerCapability ?? "YES");

  return (
    <div className="stack fade-in-up">
      <StepHeader title="What matters most?" onBack={onBack} />

      <div className="tile-grid stagger-in" role="group" aria-label="What's most important?">
        {PRIORITIES.map((p) => (
          <button key={p.value} type="button" className="tile" aria-pressed={priority === p.value} onClick={() => setPriority(p.value)}>
            <span className="tile-icon">
              <Icon name={p.icon} />
            </span>
            <span className="tile-label">{p.label}</span>
          </button>
        ))}
      </div>

      <div>
        <h3 style={{ fontSize: "1.05rem", margin: "0 0 var(--space-3)" }}>Do you have a printer?</h3>
        <div className="tile-grid" role="group" aria-label="Do you have a printer?">
          <button type="button" className="tile" aria-pressed={printer === "YES"} onClick={() => setPrinter("YES")}>
            <span className="tile-icon">
              <Icon name="printer" />
            </span>
            <span className="tile-label">Yes</span>
          </button>
          <button type="button" className="tile" aria-pressed={printer === "NO"} onClick={() => setPrinter("NO")}>
            <span className="tile-icon">
              <Icon name="printer-off" />
            </span>
            <span className="tile-label">No</span>
          </button>
          <button type="button" className="tile" aria-pressed={printer === "AUTOMATIC"} onClick={() => setPrinter("AUTOMATIC")}>
            <span className="tile-icon">
              <Icon name="check-circle" />
            </span>
            <span className="tile-label">Automatic printing set up</span>
          </button>
        </div>
      </div>

      <button
        className="btn btn-primary"
        onClick={() => onSubmit({ priority, printerCapability: printer })}
        disabled={submitting}
        aria-busy={submitting}
      >
        {submitting && <span className="spinner spinner-inline" aria-hidden="true" />}
        {submitting ? "Finding shipping options…" : "Find shipping options"}
      </button>
    </div>
  );
}
